import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { ChevronLeft, ChevronDown, Loader2, CheckCircle2, Unlock, ClipboardList, Paperclip, Upload, Camera, FileText, Download, Trash2, Flag, History, Compass, Link2, SlidersHorizontal } from "lucide-react";
import { MilestonesCard } from "@/components/MilestonesCard";
import { SourceCard, LinkedActions, Timeline, SOURCE_LABEL, ConflictBanner } from "@/components/le/ActionSections";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ST_BADGE = { "Åben": "bg-[#F1DEDA] text-[#A83232]", "I gang": "bg-[#F4EFEC] text-[#806B61]", "Lukket": "bg-[#E7EADF] text-[#5f6b4a]" };

function Card({ children, className = "", ...p }) { return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`} {...p}>{children}</div>; }
function SectionTitle({ icon: Icon, n, title, right }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="text-base font-semibold text-[#1A1919] flex items-center gap-2.5"><span className="h-7 w-7 rounded-full bg-[#F1EAE4] text-[#A83232] text-xs font-bold flex items-center justify-center">{n}</span><Icon className="h-4 w-4 text-[#806B61]" /> {title}</h2>
      {right}
    </div>
  );
}

export default function LeActionDetail() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [links, setLinks] = useState(null);
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const merge = (data) => setA((prev) => ({ ...prev, ...data }));

  const load = async () => {
    try {
      const r = await api.get(`/le/actions/${aid}`);
      setA(r.data);
      if (r.data.can_admin) api.get("/users").then((u) => setUsers(u.data)).catch(() => {});
      api.get(`/le/actions/${aid}/origin`).then((o) => {
        setOrigin(o.data);
        const s = o.data?.source;
        if (s) api.get("/le/action-links", { params: { type: s.type, id: s.id } }).then((l) => setLinks(l.data)).catch(() => {});
        else if (r.data.goal_id) api.get("/le/action-links", { params: { type: "goal", id: r.data.goal_id } }).then((l) => setLinks(l.data)).catch(() => {});
      }).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); nav("/"); }
  };
  useEffect(() => { load(); }, [aid]); // eslint-disable-line

  const followup = async (payload, okMsg = "Gemt") => {
    setBusy(true);
    try { const r = await api.post(`/le/actions/${aid}/followup`, payload); merge(r.data); toast.success(okMsg); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };
  const setResponsible = (v) => followup({ new_responsible_id: v || null }, "Ansvarlig opdateret");
  const setDue = (v) => followup({ new_deadline: v || null }, "Frist opdateret");
  const setStatusVal = (v) => { if (v === "Lukket") return; followup({ new_status: v }, "Status opdateret"); };
  const saveNote = async () => { if (!note.trim()) return toast.error("Skriv en kommentar"); if (await followup({ comment: note }, "Notat tilføjet")) setNote(""); };
  const closeAction = async () => { if (!resolution.trim()) return toast.error("Beskriv hvordan handlingen er løst"); if (await followup({ new_status: "Lukket", comment: resolution }, "Handling lukket")) setResolution(""); };
  const reopen = async () => {
    setBusy(true);
    try { const r = await api.post(`/le/actions/${aid}/reopen`, { comment: "" }); merge(r.data); toast.success("Handling genåbnet"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const uploadFile = async (fileList) => {
    const files = Array.from(fileList || []); if (!files.length) return;
    setBusy(true);
    try { for (const file of files) { const fd = new FormData(); fd.append("file", file); const r = await api.post(`/le/actions/${aid}/files`, fd); merge(r.data); } toast.success("Fil tilføjet"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const delFile = async (id) => {
    if (!window.confirm("Fjern denne fil?")) return;
    setBusy(true);
    try { const r = await api.delete(`/le/actions/${aid}/files/${id}`); merge(r.data); toast.success("Fil fjernet"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const saveMilestones = async (ms) => {
    try { const r = await api.put(`/le/actions/${aid}/milestones`, { milestones: ms }); merge(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!a) return <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div>;
  const closed = a.status === "Lukket";
  const deadlineMoves = (a.change_log || []).filter((c) => c.field === "deadline").length;
  const src = origin?.source;

  const hiddenInputs = (
    <>
      <input ref={fileRef} type="file" multiple className="hidden" data-testid="le-action-file-upload" onChange={(e) => { uploadFile(e.target.files); e.target.value = ""; }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="le-action-camera-upload" onChange={(e) => { uploadFile(e.target.files); e.target.value = ""; }} />
    </>
  );
  const uploadButtons = (sfx = "") => (
    <>
      <button type="button" data-testid={`le-action-add-file${sfx}`} onClick={() => fileRef.current?.click()} disabled={busy} className="h-9 px-3 rounded-xl border border-[#EBE8E3] text-sm text-[#806B61] flex items-center gap-1.5 hover:bg-[#FBFAF8]"><Upload className="h-4 w-4" /> Tilføj fil</button>
      <button type="button" data-testid={`le-action-add-photo${sfx}`} onClick={() => camRef.current?.click()} disabled={busy} className="h-9 px-3 rounded-xl border border-[#EBE8E3] text-sm text-[#806B61] flex items-center gap-1.5 hover:bg-[#FBFAF8] lg:hidden"><Camera className="h-4 w-4" /> Tag foto</button>
    </>
  );

  return (
    <div className="space-y-5 max-w-6xl" data-testid="le-action-detail-page">
      {hiddenInputs}
      <button onClick={() => nav(-1)} className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1" data-testid="le-action-back"><ChevronLeft className="h-4 w-4" /> Tilbage</button>

      {/* Fælles header */}
      <Card className="p-5" data-testid="le-action-header">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#A83232]">Ledelsens evaluering · Handling</span>
          {a.action_no && <span className="text-xs font-mono text-stone-400">{a.action_no}</span>}
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${ST_BADGE[a.status] || ""}`} data-testid="le-action-status-badge">{a.status}</span>
          {a.overdue && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white text-[#A83232] border border-[#E9C9C3] flex items-center gap-1"><Flag className="h-3 w-3" /> Overskredet</span>}
          {deadlineMoves > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#EFE7DC] text-[#8A6D3B] border border-[#E3D5C0]" data-testid="le-action-deadline-moved">Frist rykket {deadlineMoves}×</span>}
          {src && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F4EFEC] text-[#806B61] flex items-center gap-1" data-testid="le-action-source-chip"><Link2 className="h-3 w-3" /> Fra {SOURCE_LABEL[src.type]}{src.no ? ` ${src.no}` : ""}</span>}
        </div>
        <h1 className="text-2xl font-semibold text-[#1A1919] mt-2 whitespace-pre-wrap">{a.description}</h1>
        {a.origin?.line && <p className="text-[13px] text-stone-600 mt-1" data-testid="le-action-header-origin">{a.origin.line}</p>}
        <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-3 text-sm">
          <span className="text-stone-500">Ansvarlig <span className="text-[#1A1919] font-medium">{a.responsible_name || "—"}</span>{(a.co_responsible_names || []).length > 0 && <span className="text-stone-400"> · med {a.co_responsible_names.join(", ")}</span>}</span>
          <span className="text-stone-500">Frist <span className={`font-medium ${a.overdue ? "text-[#A83232]" : "text-[#1A1919]"}`}>{a.deadline ? daDate(a.deadline) : "Ingen frist"}</span>
            {a.original_deadline && a.original_deadline !== a.deadline && <span className="text-stone-400 line-through ml-1.5" data-testid="le-action-original-deadline">{daDate(a.original_deadline)}</span>}</span>
          <span className="text-stone-500">Oprettet af <span className="text-[#1A1919] font-medium">{a.created_by_name || "—"}</span>{a.created_at ? ` · ${daDate(a.created_at)}` : ""}</span>
        </div>
      </Card>
      <ConflictBanner message={origin?.conflict} testid="le-action-conflict" />

      <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        {/* Handlingspanel — på mobil lige under headeren, foldbart */}
        <Card className="lg:order-2 lg:sticky lg:top-4" data-testid="le-action-panel">
          <button type="button" onClick={() => setPanelOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5 lg:cursor-default" data-testid="le-action-panel-toggle">
            <span className="text-base font-semibold text-[#1A1919] flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-[#806B61]" /> Handlingspanel</span>
            <ChevronDown className={`h-4 w-4 text-stone-400 transition-transform lg:hidden ${panelOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={`px-5 pb-5 space-y-4 ${panelOpen ? "" : "hidden lg:block"}`}>
            <div className="grid grid-cols-1 gap-3">
              <div><label className="text-xs font-medium text-stone-600 mb-1 block">Ansvarlig</label>
                {a.can_admin ? <UserSingleSelect users={users} value={a.responsible_id} onChange={setResponsible} placeholder="Vælg" testid="le-action-responsible-select" className="h-10" /> : <p className="text-sm font-medium text-[#1A1919]">{a.responsible_name || "—"}</p>}</div>
              <div><label className="text-xs font-medium text-stone-600 mb-1 block">Frist</label>
                {a.can_admin ? <DanishDatePicker value={a.deadline} onChange={setDue} allowClosed testid="le-action-due-select" /> : <p className="text-sm font-medium text-[#1A1919]">{a.deadline ? daDate(a.deadline) : "Ingen frist"}</p>}</div>
              <div><label className="text-xs font-medium text-stone-600 mb-1 block">Status</label>
                <select data-testid="le-action-status-select" value={a.status} disabled={busy} onChange={(e) => setStatusVal(e.target.value)} className="w-full h-10 px-2.5 rounded-xl border border-[#EBE8E3] bg-white text-sm">
                  <option value="Åben">Åben</option><option value="I gang">I gang</option>
                  {closed && <option value="Lukket">Lukket</option>}
                </select></div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Tilføj opfølgningsnotat</label>
              <Textarea data-testid="le-action-comment" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl min-h-[70px]" placeholder="Beskriv fremdrift eller status…" />
              <div className="flex justify-end mt-2"><Button data-testid="le-action-save" onClick={saveNote} disabled={busy || !note.trim()} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">Gem notat</Button></div>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-[#F0EDE8] pt-4">{uploadButtons()}</div>
            {!closed ? (
              <div className="border-t border-[#F0EDE8] pt-4">
                <label className="text-xs font-medium text-stone-600 mb-1 block">Løsning (kræves ved lukning)</label>
                <Textarea data-testid="le-action-resolution" value={resolution} onChange={(e) => setResolution(e.target.value)} className="rounded-xl min-h-[60px]" placeholder="Beskriv hvordan handlingen er løst…" />
                <div className="flex justify-end mt-2"><Button data-testid="le-action-close" onClick={closeAction} disabled={busy} className="rounded-xl bg-[#5f6b4a] hover:bg-[#505a3e]"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Luk handling</Button></div>
              </div>
            ) : (
              <div className="border-t border-[#F0EDE8] pt-4">
                <div className="rounded-xl bg-[#F6F8F1] border border-[#D3D8C6] p-3 text-sm"><span className="font-medium text-[#5f6b4a]">Løsning:</span> <span className="text-stone-700 whitespace-pre-wrap">{a.closing_comment || "—"}</span><p className="text-xs text-stone-500 mt-1">Lukket af {a.closed_by_name || "—"} · {a.closed_at ? daDate(a.closed_at) : "—"}</p></div>
                <div className="flex justify-end mt-2"><Button variant="outline" data-testid="le-action-reopen" onClick={reopen} disabled={busy} className="rounded-xl border-[#A83232] text-[#A83232]"><Unlock className="h-4 w-4 mr-1.5" /> Genåbn</Button></div>
              </div>
            )}
          </div>
        </Card>

        <div className="space-y-5 lg:order-1 min-w-0">
          {/* 1 — Oprindelse */}
          <Card className="p-5" data-testid="le-action-origin-box">
            <SectionTitle icon={Compass} n={1} title="Oprindelse" />
            <div className="rounded-2xl border border-[#E9C9C3] bg-[#FDF8F6] p-4">
              <p className="label-caps text-[#A83232]">Beskrivelse (oprindelig)</p>
              <p className="text-[15px] text-[#1A1919] mt-2 whitespace-pre-wrap" data-testid="le-action-original-description">{a.description}</p>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-stone-500">
                <span className="text-[13px] text-stone-600" data-testid="le-action-origin-line">{a.origin?.line}</span>
                {a.goal_id && <button data-testid="le-action-open-goal" onClick={() => nav(`/maal/${a.goal_id}`)} className="text-[#A83232] hover:underline font-medium">Åbn mål</button>}
                {a.can_admin && a.origin_meeting_id && <button data-testid="le-action-open-meeting" onClick={() => nav(`/ledelse/moede/${a.origin_meeting_id}`)} className="text-[#A83232] hover:underline font-medium">Åbn referat</button>}
              </div>
            </div>
            {src && <div className="mt-3"><SourceCard source={src} current={origin.current} canAdmin={a.can_admin} /></div>}
            {links && (links.actions || []).filter((x) => x.id !== aid).length > 0 && (
              <div className="mt-4"><LinkedActions data={links} excludeId={aid} /></div>
            )}
          </Card>

          {/* 2 — Fremdrift */}
          <div data-testid="le-action-progress-section">
            <MilestonesCard milestones={a.milestones} onSave={saveMilestones} disabled={busy} testid="le-action-milestones" badge={2} />
          </div>

          {/* 3 — Filer & bilag */}
          <Card className="p-5" data-testid="le-action-files-section">
            <SectionTitle icon={Paperclip} n={3} title="Filer & bilag" right={<div className="hidden lg:flex gap-2">{uploadButtons("-section")}</div>} />
            {(a.files || []).length === 0 ? <p className="text-sm text-stone-400">Ingen filer tilføjet endnu.</p> : (
              <div className="space-y-1.5">{a.files.map((fl) => (
                <div key={fl.id} className="flex items-center gap-3 rounded-lg border border-[#EBE8E3] px-3 py-2" data-testid={`le-action-file-${fl.id}`}>
                  <FileText className="h-4 w-4 text-stone-400 shrink-0" />
                  <a href={`${BACKEND}/api/le/actions/${aid}/files/${fl.id}`} target="_blank" rel="noreferrer" className="text-sm text-[#1A1919] hover:underline truncate flex-1">{fl.original_filename}</a>
                  <span className="text-xs text-stone-400 shrink-0">{Math.max(1, Math.round((fl.size || 0) / 1024))} KB</span>
                  <a href={`${BACKEND}/api/le/actions/${aid}/files/${fl.id}`} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-[#806B61]" title="Download"><Download className="h-4 w-4" /></a>
                  <button type="button" onClick={() => delFile(fl.id)} disabled={busy} data-testid={`le-action-file-delete-${fl.id}`} className="text-stone-400 hover:text-[#A83232]" title="Fjern"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}</div>
            )}
          </Card>

          {/* 4 — Tidslinje */}
          <Card className="p-5" data-testid="le-action-timeline-section">
            <SectionTitle icon={History} n={4} title="Tidslinje" />
            <Timeline item={a} />
          </Card>
        </div>
      </div>
    </div>
  );
}
